import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { analytics } from 'analytics';
import Alert from 'ui/Alert';
import Button from 'ui/Button';
import ClipboardButton from 'ui/ClipboardButton';
import Dialog from 'ui/Dialog';
import Field from 'ui/Field';
import Group from 'ui/Group';
import Icon from 'ui/Icon';
import Input, { InputAddon } from 'ui/Input';
import { HStack, VStack } from 'ui/Layout';
import Link from 'ui/Link';
import Select, { createSelectCollection } from 'ui/Select';
import Separator from 'ui/Separator';
import { toaster } from 'ui/Toaster';
import Tooltip from 'ui/Tooltip';
import { Text } from 'ui/Typography';
import { formToSankey, hasModifiedForm } from 'utils/form';

import { useApiContext } from '../../contexts/apiContext';
import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import { PublicPermissionEnum, TeamPermissionEnum } from '../../generated-api';
import { pathToSankeyIFramed, websiteLinkTo } from '../../utils/routes';
import { useCurrentUser } from '../../utils/user';
import DialogSetting from '../DialogSetting';
import {
  FormNumberInput,
  FormSelect,
  FormSwitch,
  FormTextInput,
} from '../SankeyForm';
import UnsavedChangesAlertDialog from '../UnsavedChangesAlertDialog';

const isPublicOptions: SelectOption<PublicPermissionEnum>[] = [
  { value: 'no-access', label: 'No access', icon: 'lock' },
  { value: 'view', label: 'Can view', icon: 'eye' },
];
const isPublicCollection = createSelectCollection(isPublicOptions);

const teamPermissionOptions: SelectOption<TeamPermissionEnum>[] = [
  { value: 'no-access', label: 'No access', icon: 'lock' },
  { value: 'view', label: 'Can view', icon: 'eye' },
  { value: 'edit', label: 'Can edit', icon: 'pen' },
];

type Props = {
  children: React.ReactNode;
};

const ShareDiagramDialog: React.FC<Props> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { form: sankeyForm } = useSankeyFormContext();
  const { user } = useCurrentUser();
  const { api } = useApiContext();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const shareForm = useForm({ defaultValues: sankeyForm.getValues() });
  const { id, is_official, public_permission, team_permission } =
    shareForm.watch();

  // Check if there are any unsaved changes
  const hasUnsavedChanges = hasModifiedForm(shareForm);

  // Reset the share form when the dialog is opened (in case the changes from the last time were discarded)
  useEffect(() => {
    if (open) {
      shareForm.reset(sankeyForm.getValues());
    }
  }, [open]);

  const isOwner = user?.id === sankeyForm.getValues('owner_id');
  const hasBusinessSubscription = user?.default_team?.has_business_subscription;

  // Official diagrams must be public
  const isInvalidOfficial = !!is_official && public_permission === 'no-access';

  // Can save if there are unsaved changes and the official status is valid
  const canSave = hasUnsavedChanges && !isInvalidOfficial;

  // Can anyone view the diagram
  const canAnyoneView =
    public_permission !== 'no-access' || team_permission !== 'no-access';

  // Construct the HTML iframe code
  const iframeSrc = websiteLinkTo(pathToSankeyIFramed(id));
  const iframeCode = `<iframe src="${iframeSrc}" width="100%" height="600" frameBorder="0"></iframe>`;

  const { mutateAsync: saveDiagramSharingSettings, isLoading } = useMutation(
    async () => {
      try {
        // Call an API to create a new diagram based on the current one and
        // refetch the user to make sure permissions are up to date.
        await api().sankeys.partialUpdate({
          id,
          patchedSankeyRequest: formToSankey(shareForm),
        });
        // This might seem a little weird but it's the simplest way I found to
        // achieve want we want - update the main Sankey form to the new settings
        // and then clear the dirty fields for the share form so we don't show
        // the "pending changes" message anymore.
        sankeyForm.reset(shareForm.getValues());
        shareForm.reset(sankeyForm.getValues());
        toaster.success({ title: 'Diagram sharing settings saved' });
      } catch (error) {
        analytics.reportError('Error updating diagram sharing settings', {
          updatedDiagram: shareForm.getValues(),
          originalDiagram: sankeyForm.getValues(),
          error,
        });
      }
    }
  );

  const handleSubmit = shareForm.handleSubmit(() =>
    saveDiagramSharingSettings()
  );

  return (
    <FormProvider {...shareForm}>
      <Dialog.Root
        open={open}
        onOpenChange={(e) => {
          if (hasUnsavedChanges && !e.open) {
            setConfirmOpen(true);
          } else {
            setOpen(e.open);
          }
        }}
        size="lg"
      >
        <Dialog.Trigger asChild>{children}</Dialog.Trigger>
        <Dialog.Content>
          <form onSubmit={handleSubmit}>
            <Dialog.Header>
              <Icon fontSize="md" icon="share" mr={2} /> Share diagram
            </Dialog.Header>
            <Dialog.Body>
              <UnsavedChangesAlertDialog
                open={confirmOpen}
                setOpen={setConfirmOpen}
                discardChanges={() => {
                  // Reset form to the last saved state and close the dialog
                  shareForm.reset(sankeyForm.getValues());
                  setConfirmOpen(false);
                  setOpen(false);
                }}
              />
              <VStack alignItems="stretch" gap={6}>
                <DialogSetting
                  title="Anyone with a link"
                  description={
                    !isOwner
                      ? 'You must be the owner of this diagram to change this setting'
                      : 'Controls what other people on the internet can do with this diagram'
                  }
                  control={
                    <Select
                      value={[public_permission ?? 'no-access']}
                      onValueChange={({ value }) => {
                        shareForm.setValue(
                          'public_permission',
                          value[0] as PublicPermissionEnum,
                          { shouldDirty: true }
                        );
                      }}
                      disabled={!isOwner}
                      invalid={isInvalidOfficial}
                      collection={isPublicCollection}
                    />
                  }
                />
                <DialogSetting
                  title="Organization members"
                  description={
                    !isOwner ? (
                      'You must be the owner of this diagram to change this setting'
                    ) : !hasBusinessSubscription ? (
                      <>
                        Sharing diagrams with your organization requires a{' '}
                        <Link
                          href={websiteLinkTo('/subscriptions/')}
                          isExternal
                        >
                          Business subscription
                        </Link>
                      </>
                    ) : (
                      'Controls what other organization members can do with this diagram'
                    )
                  }
                  control={
                    <FormSelect
                      field="team_permission"
                      options={teamPermissionOptions}
                      disabled={!isOwner || !hasBusinessSubscription}
                    />
                  }
                />
                {isOwner && user?.is_official_sankey_editor && (
                  <>
                    <Separator my={1} />
                    <DialogSetting
                      title="Make diagram official"
                      description="Official diagrams are listed in the SankeyArt public diagrams page"
                      control={
                        <FormSwitch
                          invalid={isInvalidOfficial}
                          field="is_official"
                          size="lg"
                        />
                      }
                    />
                    <DialogSetting
                      title="Diagram points"
                      description="Points are used to rank diagrams in the public diagrams page"
                      control={
                        <FormNumberInput field="points" size="sm" w="70px" />
                      }
                    />
                  </>
                )}
                {isOwner && user?.is_superuser && (
                  <>
                    <Separator my={1} />
                    <DialogSetting
                      title="Make diagram a template"
                      description="Templates are used to create new diagrams"
                      control={<FormSwitch field="is_template" size="lg" />}
                    />
                    <DialogSetting
                      title="List as chart type in footer"
                      description="List this diagram as a chart type in the footer"
                      control={
                        <FormSwitch
                          field="is_listed_chart_type_in_footer"
                          size="lg"
                        />
                      }
                    />
                    <DialogSetting
                      title="Chart type name"
                      description="Name of the chart type in the footer"
                      control={
                        <FormTextInput field="chart_type_name" size="sm" />
                      }
                    />
                    <DialogSetting
                      title="Chart type list position"
                      description="Position of the chart type in the footer"
                      control={
                        <FormNumberInput
                          field="chart_type_list_position"
                          size="sm"
                          w="70px"
                        />
                      }
                    />
                  </>
                )}
                {isInvalidOfficial && (
                  <Alert
                    title="Official diagrams must be public"
                    status="error"
                    size="sm"
                  >
                    Please either make the diagram public or not official
                  </Alert>
                )}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer justifyContent="space-between">
              <HStack>
                <Tooltip
                  content={
                    canAnyoneView
                      ? "Click to copy this diagram's link to your clipboard"
                      : 'Nobody can access this diagram'
                  }
                >
                  <ClipboardButton
                    disabled={!canAnyoneView}
                    onClick={() =>
                      analytics.track('Share Dialog - Copy Link - Click')
                    }
                    value={websiteLinkTo(`/sankeys/${id}/`)}
                    variant="primary-outline"
                    size="sm"
                  >
                    <Icon icon="link" mr={1} /> Copy link
                  </ClipboardButton>
                </Tooltip>
                <Dialog.Root>
                  <Dialog.Trigger>
                    <Tooltip
                      content={
                        canAnyoneView
                          ? 'Click to embed this diagram as an HTML iframe'
                          : 'Nobody can access this diagram'
                      }
                    >
                      <Button
                        disabled={!canAnyoneView}
                        onClick={() =>
                          analytics.track('Share Dialog - Embed - Click')
                        }
                        variant="primary-outline"
                        size="sm"
                      >
                        <Icon icon="code" mr={1} /> Embed
                      </Button>
                    </Tooltip>
                  </Dialog.Trigger>
                  <Dialog.Content>
                    <Dialog.Header>Embed diagram</Dialog.Header>
                    <Dialog.Body>
                      <Field
                        label="HTML embed code"
                        helperText="Copy and paste this code into your website"
                      >
                        <Group attached w="100%">
                          <Input value={iframeCode} readOnly />
                          <InputAddon
                            cursor="pointer"
                            onClick={() => {
                              navigator.clipboard.writeText(iframeCode);
                              toaster.success({
                                title: 'Embed code copied to clipboard',
                              });
                            }}
                          >
                            <Icon icon="copy-regular" />
                          </InputAddon>
                        </Group>
                      </Field>
                    </Dialog.Body>
                  </Dialog.Content>
                </Dialog.Root>
              </HStack>
              <HStack gap={6}>
                {hasUnsavedChanges && (
                  <Text
                    color="gray.600"
                    display={{ base: 'none', sm: 'block' }}
                    fontStyle="italic"
                  >
                    Pending changes
                  </Text>
                )}
                <Button
                  type="submit"
                  variant="primary-solid"
                  loading={isLoading}
                  disabled={!canSave}
                >
                  Save
                </Button>
              </HStack>
            </Dialog.Footer>
          </form>
        </Dialog.Content>
      </Dialog.Root>
    </FormProvider>
  );
};

export default ShareDiagramDialog;
